import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { TABLE_EDITABLE_CELL } from "const"
import {
    QidDatapoint,
    QidModelFailureMode,
} from "server/models"
import {
    StoreType,
    setQidDatapointNew,
    setQidDatapointActiveCell,
    setQidDatapointDeleteExisting,
} from "client/store"
import {
    TableWrapper,
    TableScroller,
    TableContentWrapper,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableCellContentWrapper,
    TableCellContent,
    Empty,
    LinkButton,
} from "client/components"
import {
    TableRowStyled,
    TableCellAddWrapper,
    AddSpan,
    NewStatus,
    DeleteStatus,
} from "./styled"

import Columns from "./columns"

const rowSize = "sm"

interface QidDatapointEditTable {
    failureModes: readonly QidModelFailureMode[]
    onAdd: () => void
}

const EditTable = ({
    failureModes,
    onAdd,
}: QidDatapointEditTable) => {
    const newDatapoints = useSelector((state: StoreType) => state.qidDatapoint.new)
    const existingDatapoints = useSelector((state: StoreType) => state.qidDatapoint.existing)
    const activeCell = useSelector((state: StoreType) => state.qidDatapoint.activeCell)
    const changes = useSelector((state: StoreType) => state.qidDatapoint.changes)
    const deleteExisting = useSelector((state: StoreType) => state.qidDatapoint.deleteExisting)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [newDatapointsArray, setNewDatapointsArray] = useState<QidDatapoint[]>([])
    const [existingDatapointsArray, setExistingDatapointsArray] = useState<QidDatapoint[]>([])

    const clearActiveCell = () => {
        dispatch(setQidDatapointActiveCell(null))
    }

    useEffect(() => {
        document.addEventListener("click", clearActiveCell)
        return () => document.removeEventListener("click", clearActiveCell)
    }, [])

    useEffect(() => {
        const newArray: QidDatapoint[] = []
        const existingArray: QidDatapoint[] = []

        newDatapoints.forEach((datapoint) => {
            newArray.push(datapoint)
            datapoint.datapoints.forEach((nested) => {
                if (nested.failureMode) newArray.push(nested)
            })
        })

        existingDatapoints.forEach((datapoint) => {
            existingArray.push(datapoint)
            datapoint.datapoints.forEach((nested) => {
                if (nested.failureMode) existingArray.push(nested)
            })
        })

        setNewDatapointsArray(newArray)
        setExistingDatapointsArray(existingArray)
    }, [newDatapoints, existingDatapoints])

    const deleteCreated = (datapointId: number) => {
        const datapointsFiltered = newDatapoints.filter((datapoint) => datapoint.id !== datapointId)
        dispatch(setQidDatapointNew(datapointsFiltered))
    }

    const toggleDeleteExisting = (datapointId: number) => {
        if (deleteExisting.includes(datapointId)) {
            const deleteFiltered = deleteExisting.filter((id) => id !== datapointId)
            dispatch(setQidDatapointDeleteExisting(deleteFiltered))
        } else {
            const deleteCopy = deleteExisting.slice()
            deleteCopy.push(datapointId)
            dispatch(setQidDatapointDeleteExisting(deleteCopy))
        }
    }

    const columns = Columns(failureModes.length > 0)

    return (
        <TableWrapper>
            <TableScroller>
                <TableContentWrapper>
                    <thead>
                        <TableRow size={rowSize} noHover>
                            {
                                columns.map((column) => (
                                    <TableHeaderCell
                                        key={column.field}
                                        cellWidth={column.cellWidth || null}
                                        cellMinWidth={column.cellMinWidth || null}
                                        cellMaxWidth={column.cellMaxWidth || null}
                                    >
                                        {
                                            column.header
                                            && <span>{column.header}</span>
                                        }
                                    </TableHeaderCell>
                                ))
                            }
                        </TableRow>
                    </thead>
                    <tbody>
                        <TableRow size={rowSize}>
                            <TableCellAddWrapper colSpan={100} centerAlign onClick={onAdd}>
                                <TableCellContentWrapper>
                                    <TableCellContent>
                                        <AddSpan>{lang.add}</AddSpan>
                                    </TableCellContent>
                                </TableCellContentWrapper>
                            </TableCellAddWrapper>
                        </TableRow>

                        {
                            newDatapointsArray.map((newDatapoint) => {
                                const changedDatapoint = changes[newDatapoint.id] || {}
                                const datapoint = {
                                    ...newDatapoint,
                                    ...changedDatapoint,
                                }

                                return (
                                    <TableRowStyled
                                        key={datapoint.id}
                                        size={rowSize}
                                        newRow
                                    >
                                        {
                                            columns.map((column) => {
                                                // don't display status or action for nested rows
                                                const isParent = !datapoint.failureMode
                                                const isEditable = activeCell
                                                    && activeCell.datapointId === datapoint.id
                                                    && activeCell.field === column.field

                                                return (
                                                    <TableCell
                                                        key={column.field}
                                                        className={isEditable && TABLE_EDITABLE_CELL}
                                                        isEditable={isEditable}
                                                        cellWidth={column.cellWidth || null}
                                                        cellMinWidth={column.cellMinWidth || null}
                                                        cellMaxWidth={column.cellMaxWidth || null}
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            if (column.editAccess && column.editAccess(datapoint)) {
                                                                dispatch(setQidDatapointActiveCell({
                                                                    datapointId: datapoint.id,
                                                                    field: column.field,
                                                                }))
                                                            } else {
                                                                dispatch(setQidDatapointActiveCell(null))
                                                            }
                                                        }}
                                                    >
                                                        <TableCellContentWrapper>
                                                            <TableCellContent>
                                                                {
                                                                    column.field === "status"
                                                                    && isParent
                                                                    && <NewStatus>{lang.new}</NewStatus>
                                                                }
                                                                {
                                                                    column.field === "delete"
                                                                    && isParent
                                                                    && (
                                                                        <LinkButton onClick={() => deleteCreated(datapoint.id)}>
                                                                            {lang.delete}
                                                                        </LinkButton>
                                                                    )
                                                                }
                                                                {
                                                                    isEditable
                                                                    && column.focusedFormatter
                                                                        ? column.focusedFormatter(datapoint)
                                                                        : column.formatter && column.formatter(datapoint)
                                                                }
                                                            </TableCellContent>
                                                        </TableCellContentWrapper>
                                                    </TableCell>
                                                )
                                            })
                                        }
                                    </TableRowStyled>
                                )
                            })
                        }
                        {
                            existingDatapointsArray.map((existingDatapoint) => {
                                const changedDatapoint = changes[existingDatapoint.id] || {}
                                const datapoint = {
                                    ...existingDatapoint,
                                    ...changedDatapoint,
                                }

                                return (
                                    <TableRowStyled
                                        key={datapoint.id}
                                        size={rowSize}
                                        deleteRow={
                                            deleteExisting.includes(datapoint.id)
                                            || deleteExisting.includes(datapoint.datapointId)
                                        }
                                    >
                                        {
                                            columns.map((column) => {
                                                // don't display status or action for nested rows
                                                const isParent = !datapoint.failureMode
                                                const isEditable = activeCell
                                                    && activeCell.datapointId === datapoint.id
                                                    && activeCell.field === column.field

                                                return (
                                                    <TableCell
                                                        key={column.field}
                                                        className={isEditable && TABLE_EDITABLE_CELL}
                                                        isEditable={isEditable}
                                                        cellWidth={column.cellWidth || null}
                                                        cellMinWidth={column.cellMinWidth || null}
                                                        cellMaxWidth={column.cellMaxWidth || null}
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            if (column.editAccess && column.editAccess(datapoint)) {
                                                                dispatch(setQidDatapointActiveCell({
                                                                    datapointId: datapoint.id,
                                                                    field: column.field,
                                                                }))
                                                            } else {
                                                                dispatch(setQidDatapointActiveCell(null))
                                                            }
                                                        }}
                                                    >
                                                        <TableCellContentWrapper>
                                                            <TableCellContent>
                                                                {
                                                                    column.field === "status"
                                                                    && isParent
                                                                    && deleteExisting.includes(datapoint.id)
                                                                    && <DeleteStatus>{lang.toDelete}</DeleteStatus>
                                                                }
                                                                {
                                                                    column.field === "delete"
                                                                    && isParent
                                                                    && (
                                                                        <LinkButton onClick={() => toggleDeleteExisting(datapoint.id)}>
                                                                            {
                                                                                deleteExisting.includes(datapoint.id)
                                                                                    ? lang.unmark
                                                                                    : lang.markToDelete
                                                                            }
                                                                        </LinkButton>
                                                                    )
                                                                }
                                                                {
                                                                    isEditable && column.focusedFormatter
                                                                        ? column.focusedFormatter(datapoint)
                                                                        : column.formatter && column.formatter(datapoint)
                                                                }
                                                            </TableCellContent>
                                                        </TableCellContentWrapper>
                                                    </TableCell>
                                                )
                                            })
                                        }
                                    </TableRowStyled>
                                )
                            })
                        }
                        {
                            newDatapoints.length === 0
                            && existingDatapoints.length === 0
                            && (
                                <TableRow size="sm">
                                    <TableCell colSpan={100}>
                                        <TableCellContentWrapper>
                                            <Empty />
                                        </TableCellContentWrapper>
                                    </TableCell>
                                </TableRow>
                            )
                        }
                    </tbody>
                </TableContentWrapper>
            </TableScroller>
        </TableWrapper>
    )
}
export default EditTable
